import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u6_x4_frost';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einführung',
    body: 'Der Frostschutzthermostat ist eine kritische Sicherheitseinrichtung in Lüftungsanlagen mit wasserführenden Wärmetauschern (Erhitzern). Er verhindert das Einfrieren und somit die Zerstörung des Erhitzers durch zu niedrige Lufttemperaturen.'
  },
  {
    heading: 'Funktionsweise (zweistufig)',
    body: 'Die Temperaturerfassung erfolgt über ein langes Kapillarrohr.\n\n1. Stufe (z. B. +10°C): Heizventil zwangsweise AUF, Heizungspumpe EIN, Anlage läuft weiter; Meldung "Frostschutz Stufe 1" (pendelnd/auto-Quittierung).\n\n2. Stufe (z. B. +5°C): Lüftungsanlage AUS, Außen-/Fortluftklappen ZU, Heizventil AUF, Pumpe EIN; Alarm "Frostschutz Stufe 2 – Anlage AUS" (verriegelnd, manuelle Quittierung erforderlich).'
  },
  {
    heading: 'Einbau des Kapillarrohrs',
    body: 'Kapillarrohr (typ. 2–6 m) luftseitig nach dem Lufterhitzer mäanderförmig verlegen; kälteste Stelle erfassen; nicht knicken; Montageklammern verwenden. Reaktion erfolgt auf die niedrigste Temperatur entlang der aktiven Länge (nicht nur Punktmessung).'
  },
  {
    heading: 'Elektrischer Anschluss & Meldung',
    body: 'Potentialfreie Schaltkontakte (meist Öffner/Wechsler) je Stufe.\nStufe 1 an DI der DDC (logische Maßnahmen).\nStufe 2 zusätzlich direkt in Sicherheitskette (z. B. Ventilatorschütze), parallel Meldung an DI der DDC. DDC visualisiert Meldungen/Alarme.'
  },
  {
    heading: 'Fazit',
    body: 'Ein korrekt installierter, eingestellter und regelmäßig geprüfter Frostschutzthermostat schützt Lufterhitzer zuverlässig und erhöht die Betriebssicherheit der Lüftungsanlage.'
  }
];

const images = [
  { id: 'img_frost_thermostat_beispiel', label: 'Frostschutzthermostat', src: `${IMAGE_BASE}sicherheit_jtf_25.webp`, alt: 'Frostschutzthermostat mit Kapillarrohr' },
  { id: 'img_frost_montage_kapillarrohr', label: 'Kapillarrohr Montage', src: `${IMAGE_BASE}sicherheit_frost_eingebaut.jpg`, alt: 'Verlegung Kapillarrohr hinter Lufterhitzer' },
  { id: 'img_frost_schaltplan_anschluss', label: 'Schaltplan (Pumpe)', src: `${IMAGE_BASE}schaltplan_erh_pumpe.PNG`, alt: 'Prinzipschaltplan: Erhitzer mit Pumpe' },
  { id: 'img_frost_schaltplan_anschluss2', label: 'Schaltplan (Frost)', src: `${IMAGE_BASE}schaltplan_frost.PNG`, alt: 'Prinzipschaltplan: Frostschutzkette' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.6.4 Frostschutzthermostat', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u6_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


